import React from 'react';
import { motion } from 'framer-motion';
import { ClockIcon, PhoneIcon, PhoneMissedIcon, PhoneOutgoingIcon } from 'lucide-react';
import { TrendDelta } from '../common/TrendDelta';
import { Sparkline } from '../common/Sparkline';
import { AnimatedNumber } from '../common/AnimatedNumber';
import { RevealItem, revealContainer } from '../common/Reveal';
import { formatDuration, formatNumber, formatPercent } from '../../utils/format';
import type { AgentStats, KpiTrend, VolumePoint } from '../../types/voip';
interface HomeKpisProps {
  totals: AgentStats;
  trend: KpiTrend;
  volume: VolumePoint[];
  teamSeries: number[];
  previousLabel: string;
  onOpen: () => void;
}
const wholeNumber = (n: number) => formatNumber(Math.round(n));
export function HomeKpis({
  totals,
  trend,
  volume,
  teamSeries,
  previousLabel,
  onOpen
}: HomeKpisProps) {
  const cards = [{
    label: 'Total talk time',
    num: totals.talkSeconds,
    fmt: formatDuration,
    delta: trend.talkSeconds,
    Icon: ClockIcon,
    icon: 'bg-accent-soft text-accent',
    spark: teamSeries,
    tone: 'bright' as const
  }, {
    label: 'Outbound calls',
    num: totals.outbound,
    fmt: wholeNumber,
    delta: trend.outbound,
    Icon: PhoneOutgoingIcon,
    icon: 'bg-bright/10 text-bright',
    spark: volume.map(p => p.outbound),
    tone: 'bright' as const
  }, {
    label: 'Answer rate',
    num: totals.answerRate,
    fmt: formatPercent,
    delta: trend.answerRate,
    Icon: PhoneIcon,
    icon: 'bg-positive/10 text-positive',
    spark: volume.map(p => p.inbound + p.missed === 0 ? 1 : p.inbound / (p.inbound + p.missed)),
    tone: 'positive' as const
  }, {
    label: 'Missed calls',
    num: totals.missed,
    fmt: wholeNumber,
    delta: trend.missed,
    invert: true,
    Icon: PhoneMissedIcon,
    icon: 'bg-danger/10 text-danger',
    spark: volume.map(p => p.missed),
    tone: 'danger' as const
  }];
  return <motion.section variants={revealContainer} aria-label="Key metrics" className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4" data-id="element-274" data-mp="1c2lm3wptfdiw">
      {cards.map(({
      label,
      num,
      fmt,
      delta,
      invert,
      Icon,
      icon,
      spark,
      tone
    }) => <RevealItem key={label} fill data-id="element-275" data-mp="131dqjmeooqsi">
          <button type="button" onClick={onOpen} aria-label={`${label}: ${fmt(num)}. Open Reports`} className="group flex flex-col rounded-2xl border border-line bg-surface p-6 text-left shadow-card transition-[transform,box-shadow] duration-200 ease-out hover:-translate-y-0.5 hover:shadow-lift active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent" data-id="element-276" data-mp="y4drvwk5yqbf">
            <div className="flex w-full items-start justify-between gap-4" data-id="element-277" data-mp="ff78067mvso1">
              <span className={`flex h-12 w-12 items-center justify-center rounded-full transition-transform duration-200 ease-out group-hover:scale-105 ${icon}`} data-id="element-278" data-mp="aet14rhrqorl">
                <Icon className="h-5 w-5" aria-hidden data-id="element-279" data-mp="3rgxg7sxgszq3" />
              </span>
              <Sparkline data={spark} tone={tone} width={96} height={36} data-id="element-280" data-mp="3rj05ubupgqua" />
            </div>
            <p className="mt-5 font-display text-[34px] font-bold leading-none tracking-tight text-ink" data-id="element-281" data-mp="8rpy6a5hkov">
              <AnimatedNumber value={num} format={fmt} data-id="element-282" data-mp="57mg5tag6bj0" />
            </p>
            <p className="mt-2 text-sm text-muted" data-id="element-283" data-mp="a7h9s4llxf7r">{label}</p>
            <div className="mt-3 flex items-center gap-1.5" data-id="element-284" data-mp="38qo1xkoelhi1">
              <TrendDelta value={delta} invert={invert} data-id="element-285" data-mp="38obdqps1yawj" />
              <span className="truncate text-xs text-subtle" data-id="element-286" data-mp="3dqk9tv081t2v">vs {previousLabel}</span>
            </div>
          </button>
        </RevealItem>)}
    </motion.section>;
}